import ora from 'ora';
import dayjs from 'dayjs';
import TestExclude from 'test-exclude';
import * as fs from 'fs';
import * as path from 'path';
import * as reports from 'istanbul-reports';
import * as libReport from 'istanbul-lib-report';
import * as servicesApi from '../commons/testimServicesApi';
import { ArgError } from '../errors';
import { promiseMap } from '../utils';
import { getLogger } from '../commons/logger';
import { lazyRequire } from '../commons/lazyRequire';
import { SummaryToObjectReport } from './SummaryToObjectReport';
import type { RawSourceMap } from 'source-map';
import type { ProcessCov } from '@bcoe/v8-coverage';
import type { CoverageMap, CoverageSummaryData } from 'istanbul-lib-coverage';
import type { RunnerOptions } from '../runOptions';
import type { RawTestResult } from 'services/src/commons/mongo/models/testResult';
import type { CDPMethodResponse } from 'clickim/src/background/cdp/CDPTypes';

type FunctionCoverage = CDPMethodResponse['Profiler.takePreciseCoverage']['result'][number]['functions'];
type SourceMapType = 'inline' | 'file';
interface SourceMap { sourcemap: RawSourceMap; toObject(): { sourcesContent: string[]; sources: string[] } }
interface CoverageReport { scriptId: string; text?: string; url: string; sourceUrl: string; hasSourceURL?: boolean; sourceMapType?: SourceMapType; functions: FunctionCoverage; hash: string }
const logger = getLogger('test-run-status');

const convertToURL = (_path: string): Partial<URL> => {
    try {
        return new URL(_path);
    } catch (err) {
        try {
            return new URL(`file://${_path}`);
        } catch (e) {
            return {};
        }
    }
};

const pathHasQueryParam = (_path: string) => {
    const urlObj = convertToURL(_path);
    return !!urlObj.search;
};

const parsePath = (_path: string) => {
    if (!_path) {
        return '';
    }
    const urlObj = convertToURL(_path);
    return urlObj.pathname!.substring(1);
};

const excludePath = (codeCoverageInclude: string[], _path: string) => {
    const pathname = parsePath(_path);
    const exclude = new TestExclude({
        relativePath: false,
        include: codeCoverageInclude,
    });

    return !exclude.shouldInstrument(pathname);
};

const removeAfterQuestionMark = (str: string) => str.substring(0, str.indexOf('?'));

const getSourceMap = async ({ source, sourceMapDir, sourceMapType }: { sourceMapType?: SourceMapType; source: string; sourceMapDir?: string }): Promise<SourceMap> => {
    const convertSourceMap = await lazyRequire('convert-source-map');
    if (sourceMapType === 'file' && !sourceMapDir) {
        throw new ArgError('--code-coverage-source-map-path [path]');
    }
    return sourceMapDir ?
        convertSourceMap.fromMapFileSource(source, sourceMapDir) :
        convertSourceMap.fromSource(source);
};

const rewritePath = (storagePath: string, fileUrl: string) => {
    const parsedPath = path.resolve(storagePath, parsePath(fileUrl));
    return `${parsedPath}.js`;
};

const remapCoverage = async (options: RunnerOptions, storagePath: string, sourceMaps: { [url: string]: SourceMap }) => {
    const { codeCoverageInclude } = options;
    await promiseMap(Object.values(sourceMaps), async (sourceMap) => {
        if (!sourceMap) {
            return;
        }
        const sourceMapObject = sourceMap.toObject();
        await promiseMap(sourceMapObject.sources, async (sourcePath, index) => {
            if (excludePath(codeCoverageInclude, sourcePath)) {
                return;
            }
            const parsedPath = rewritePath(storagePath, sourcePath);
            await fs.promises.mkdir(path.parse(parsedPath).dir, { recursive: true });
            await fs.promises.writeFile(parsedPath, sourceMapObject.sourcesContent[index]);
        });
    });
};

const htmlReportLinkMapper: reports.LinkMapper = {
    getPath(node) {
        if (typeof node === 'string') {
            return node;
        }
        let filePath = (node as any).getQualifiedName();
        if ((node as any).isSummary()) {
            if (filePath !== '') {
                filePath += '/index.html';
            } else {
                filePath = 'index.html';
            }
        } else {
            if (pathHasQueryParam(filePath)) {
                filePath = removeAfterQuestionMark(filePath);
            }
            filePath += '.html';
        }
        return filePath;
    },

    relativePath(source, target) {
        const targetPath = this.getPath(target);
        const sourcePath = path.dirname(this.getPath(source));
        return path.posix.relative(sourcePath, targetPath);
    },

    assetPath(node, name) {
        return this.relativePath(this.getPath(node), name);
    },
};

const saveCoverageReports = async (options: RunnerOptions, coverageMap: CoverageMap, reportDir: string, tempJsDir: string) => {
    const { codeCoverageReporter } = options;
    const reportSummary = {};
    const context = libReport.createContext({
        dir: reportDir,
        coverageMap,
        watermarks: libReport.getDefaultWatermarks(),
        sourceFinder: (filePath) => {
            try {
                const parsedPath = rewritePath(tempJsDir, filePath);
                return fs.readFileSync(parsedPath, 'utf8');
            } catch (ex) {
                throw new Error(`Unable to lookup source: ${filePath} (${ex.message})`);
            }
        },
    });

    (new SummaryToObjectReport({ appendToObject: reportSummary })).execute(context);
    if (Array.isArray(codeCoverageReporter)) {
        codeCoverageReporter.forEach((reporter) => {
            let cfg: Partial<reports.ProjectOptions | reports.HtmlOptions> = { projectRoot: '/' };
            if (reporter === 'html') {
                cfg = { linkMapper: htmlReportLinkMapper };
            }
            reports.create(reporter, cfg).execute(context);
        });
    }
    return reportSummary as CoverageSummaryData;
};

const convertV8ToIstanbul = async (options: RunnerOptions, { source, sourceMap, functions }: { sourceMap?: SourceMap; source: string; functions: ProcessCov['result'][number]['functions'] }) => {
    const { codeCoverageInclude } = options;
    if (!sourceMap || sourceMap.sourcemap.sources.length === 0) {
        return undefined;
    }
    const v8toIstanbul = await lazyRequire('v8-to-istanbul') as typeof import('v8-to-istanbul');
    const converter = v8toIstanbul('FAKE_PATH', 0, {
        source,
        sourceMap: sourceMap as any,
    }, (_path) => excludePath(codeCoverageInclude, _path));
    await converter.load();
    converter.applyCoverage(functions);
    return converter.toIstanbul();
};

const collectAndMergeJsCoverageData = async (projectId: string, runId: string) => {
    const DOWNLOAD_COVERAGE_DATA_CONCURRENCY = 20;
    const { mergeProcessCovs } = await lazyRequire('@bcoe/v8-coverage') as typeof import('@bcoe/v8-coverage');
    let mergedCoverages: ProcessCov = { result: [] };

    const covUrlMap = new Map<string, { text: string; url: string; sourceMapType?: SourceMapType; hash: string }>();
    const realDataRes = await servicesApi.getRealData(projectId, 'testResult', `runId=${runId}`);
    const testResults = realDataRes.data.docs as unknown as RawTestResult[];

    await promiseMap(
        testResults.flatMap((testResult) => testResult.JSCoverageURLS || []),
        async (coverageURL) => {
            const data = await servicesApi.getS3Artifact<CoverageReport[]>(coverageURL, 90000);
            await promiseMap(data, async (cov) => {
                if (!covUrlMap.has(cov.url)) {
                    let text = cov.text!;
                    if (cov.sourceUrl) {
                        // set temp value to reduce read S3 file
                        covUrlMap.set(cov.url, 'TEMP' as any);
                        text = await servicesApi.getS3ArtifactText(cov.sourceUrl);
                    }
                    covUrlMap.set(cov.url, {
                        text,
                        url: cov.url,
                        sourceMapType: cov.sourceMapType,
                        hash: cov.hash,
                    });
                }
                delete cov.text;
                mergedCoverages = mergeProcessCovs([mergedCoverages, { result: [cov] }]);
            });
        },
        { concurrency: DOWNLOAD_COVERAGE_DATA_CONCURRENCY },
    );

    return { covUrlMap, mergedCoverages };
};

export const calculateCoverage = async (options: RunnerOptions, branchToUse: string, numOfTests: number, runId: string) => {
    if (!options.collectCodeCoverage) {
        return undefined;
    }
    logger.info('start js coverage process');
    const spinner = ora(`analyzing coverage for ${numOfTests} ${numOfTests === 1 ? 'test' : 'tests'}`).start();
    const baseDir = path.resolve(options.codeCoverageReportPath || './coverage');
    const tempJsDir = path.resolve(baseDir, `.js/${dayjs().format('DDMMYYYYHHmmss')}`);
    const sourceMapDir = options.codeCoverageSourceMapPath ? path.resolve(options.codeCoverageSourceMapPath) : undefined;
    try {
        const [libCoverage, { mergedCoverages, covUrlMap }] = await Promise.all([
            lazyRequire('istanbul-lib-coverage') as Promise<typeof import('istanbul-lib-coverage')>,
            collectAndMergeJsCoverageData(options.project, runId),
        ]);

        if (mergedCoverages.result.length === 0) {
            spinner.fail('Failed to report coverage information - js code coverage is empty');
            return undefined;
        }
        logger.info('start js coverage merge and remap', { numOfFiles: covUrlMap.size, numMergedCoverages: mergedCoverages.result.length });
        const coverageMap = libCoverage.createCoverageMap({});

        const sourceMaps: { [url: string]: SourceMap } = {};
        await promiseMap(mergedCoverages.result, async ({ url, functions }) => {
            const { text: source, sourceMapType } = covUrlMap.get(url)!;

            const sourceMap = await getSourceMap({ sourceMapType, source, sourceMapDir });
            sourceMaps[url] = sourceMap;

            const istanbulCoverage = await convertV8ToIstanbul(options, { source, sourceMap, functions });
            coverageMap.merge(istanbulCoverage!);
        });
        await remapCoverage(options, tempJsDir, sourceMaps);
        const coverageSummary = await saveCoverageReports(options, coverageMap, baseDir, tempJsDir);
        spinner.succeed();
        return coverageSummary;
    } catch (err) {
        const baseMsg = 'Failed to report coverage information';
        if (err instanceof ArgError) {
            spinner.fail(`${baseMsg}, missing arg: ${err.message}`);
        } else {
            spinner.fail(baseMsg);
        }
        logger.error(baseMsg, { err });
    }

    return undefined;
};
